import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { lightThemeOptions } from './lightTheme';
import { darkThemeOptions } from './darkTheme';

/**
 * Common theme configuration shared between light and dark modes
 */
const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: "'Open Sans', 'Nunito', sans-serif",

    // --- Headings (Nunito) ---
    h1: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '3rem',       // 48px
      fontWeight: 400,
      lineHeight: 1.167,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '2.375rem',   // 38px
      fontWeight: 500,
      lineHeight: 1.26,
      letterSpacing: '-0.25px',
    },
    h3: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1.75rem',    // 28px
      fontWeight: 500,
      lineHeight: 1.285,
      letterSpacing: '0px',
    },
    h4: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1.25rem',    // 20px
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '0.15px',
    },
    h5: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1.125rem',   // 18px
      fontWeight: 700,
      lineHeight: 1.555,
      letterSpacing: '0.15px',
    },
    h6: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },

    // --- Body (Open Sans) ---
    caption: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px — maps to body3 (spec has no separate caption token)
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '0.15px',
    },
    body1: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    body1SemiBold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    body1Bold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 700,        // Bold
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    body2: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.10px',
    },
    subtitle2: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 500,
      lineHeight: 1.43,
      letterSpacing: '0.10px',
    },
    overline: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
    },
    overline2: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px — Button Medium default
      fontWeight: 500,
      lineHeight: 1.43,
      letterSpacing: '0.3px',
      textTransform: 'none',
    },

    // --- Button Typography Variants ---
    buttonLarge: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',       // 16px
      fontWeight: 500,        // Medium
      lineHeight: '26px',     // Raw 26px for button alignment
      letterSpacing: '0.4px',
      textTransform: 'none',
    },
    buttonMedium: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 500,        // Medium
      lineHeight: '24px',     // Raw 24px for button alignment
      letterSpacing: '0.3px',
      textTransform: 'none',
    },
    buttonSmall: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 500,        // Medium
      lineHeight: '22px',     // Raw 22px for button alignment
      letterSpacing: '0.3px',
      textTransform: 'none',
    },

    // --- Custom variants ---
    body1Medium: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '1rem',         // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    h4Regular: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1.25rem',    // 20px
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '0.15px',
    },
    body2SemiBold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 600,
      lineHeight: 1.43,
      letterSpacing: '0.15px',
    },
    body2Bold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 700,
      lineHeight: 1.43,
      letterSpacing: '0.15px',
    },
    listSubheader: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 500,
      lineHeight: '48px',     // strict 48px box height
      letterSpacing: '0.10px',
    },
    body3: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 400,
      lineHeight: 1.33,
      letterSpacing: '0.15px',
    },
    body3Medium: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 500,
      lineHeight: '20px',     // Raw 20px for badge/tooltip alignment
      letterSpacing: '0.15px',
    },
    body3SemiBold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 600,
      lineHeight: 1.33,
      letterSpacing: '0.15px',
    },
    body3Bold: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.75rem',    // 12px
      fontWeight: 700,
      lineHeight: 1.33,
      letterSpacing: '0.15px',
    },
    body4: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.625rem',   // 10px
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0.50px',
    },
    overline1: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 600,        // Semi Bold
      lineHeight: 1.33,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
    },
    overline3: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '0.625rem',   // 10px
      fontWeight: 600,        // Semi Bold
      lineHeight: 1.4,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    navLabel: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.15px',
    },
    navLabelActive: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '0.875rem',   // 14px
      fontWeight: 600,
      lineHeight: 1.43,
      letterSpacing: '0.15px',
    },
    navGroupLabel: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '0.625rem',   // 10px
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    },
    displayMd: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '1.875rem',   // 30px — KPI hero metric values
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0px',
    },
    displayLarge: {
      fontFamily: "'Nunito', sans-serif",
      fontSize: '6rem',       // 96px — Large hero text for error states
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-1.5px',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontFamily: "'Open Sans', sans-serif",
          fontWeight: 500,
          textTransform: 'none',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          fontSize: '0.75rem',   // 12px — Button Small
          lineHeight: 1.33,
          letterSpacing: '0.3px',
        },
        sizeMedium: {
          fontSize: '0.875rem',  // 14px — Button Medium
          lineHeight: 1.43,
          letterSpacing: '0.3px',
        },
        sizeLarge: {
          fontSize: '1rem',      // 16px — Button Large
          lineHeight: 1.5,
          letterSpacing: '0.4px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: 'none',
        },
        elevation2: {
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(5, 40, 56, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(5, 40, 56, 0.2)',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 400,
          color: '#6c757d',
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600,
          },
        }),
      },
    },
  },
};

export const createLightTheme = (): Theme => {
  return createTheme({
    ...commonThemeOptions,
    ...lightThemeOptions,
  });
};

export const createDarkTheme = (): Theme => {
  return createTheme({
    ...commonThemeOptions,
    ...darkThemeOptions,
  });
};

export const getTheme = (mode: 'light' | 'dark'): Theme => {
  return mode === 'light' ? createLightTheme() : createDarkTheme();
};

/**
 * Icon size tokens from design system
 * Per docs/design/04-icons.md specification
 */
export const iconSizes = {
  sm: 18,  // 1.125rem - Small inline chips, dense table cells, nested menus
  md: 24,  // 1.5rem - Default standard: buttons, forms, page actions, side nav
  lg: 32,  // 2rem - Empty states, upload zones, KPI headers
};
